import { useEffect, useState, type ReactNode, type CSSProperties } from 'react';
import {
  useSmtpMailboxes,
  useCreateSmtpMailbox,
  useDeleteSmtpMailbox,
  useRetestSmtpMailbox,
  useUpdateSmtpMailbox,
  useSyncMailboxCalendarLink,
  startOAuthConnect,
  type SmtpMailbox,
  type CreateMailboxPayload,
  type MailboxStatus,
} from '../../api/smtp-mailboxes.api';
import { useAuthStore } from '../../stores/auth.store';
import { useToast } from '../../components/ui/Toast';

const DAILY_LIMIT_WARNING = 50;
const DEFAULT_DAILY_LIMIT = 50;
const MIN_DAILY_LIMIT = 5;
const MAX_DAILY_LIMIT = 100;

/* ── Section 10B mailboxes — outreach SMTP connections ── */

/** Human-readable error messages for the ?error=... codes the OAuth callback redirects with. */
const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  no_refresh_token: 'The provider did not return a refresh token. Please try connecting again and grant all requested permissions.',
  no_email_on_account: 'Could not read an email address from that account.',
  mailbox_limit_reached: 'Each person can connect one Microsoft 365 or Google mailbox. Remove the one already connected first.',
  expired_state: 'The connection request expired. Please try again.',
  invalid_state: 'The connection could not be verified. Please try again.',
  provider_not_configured: 'This provider is not configured on the server yet.',
};

export function MailboxesSection() {
  const { data, isLoading } = useSmtpMailboxes();
  const [modalOpen, setModalOpen] = useState(false);
  const [connecting, setConnecting] = useState<null | 'microsoft' | 'google'>(null);
  const { addToast } = useToast();
  const mailboxes = data?.data ?? [];

  // Surface the result of the OAuth round-trip (?connected=1 / ?error=code),
  // then strip the params so a refresh doesn't re-toast.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('connected')) {
      addToast({ variant: 'success', title: 'Mailbox connected', description: 'Verifying the connection now…' });
    } else if (params.has('error')) {
      const code = params.get('error') ?? '';
      addToast({
        variant: 'danger',
        title: 'Could not connect mailbox',
        description: OAUTH_ERROR_MESSAGES[code] ?? 'Something went wrong connecting the mailbox. Please try again.',
      });
    } else {
      return;
    }
    params.delete('connected');
    params.delete('error');
    const search = params.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${search ? `?${search}` : ''}`);
  }, [addToast]);

  const handleOAuthConnect = async (provider: 'microsoft' | 'google') => {
    setConnecting(provider);
    try {
      await startOAuthConnect(provider);
      // startOAuthConnect navigates away on success; if we're still here it failed.
    } catch {
      addToast({ variant: 'danger', title: 'Could not start sign-in', description: 'Please try again in a moment.' });
      setConnecting(null);
    }
  };

  return (
    <Shell
      title="Mailboxes (outreach only)"
      subtitle="Personal/shared connections used by outreach campaigns (Candidate, BD, Job, Deal, Event). Newsletter / bulk mail never sends from these: it routes through a verified marketing domain via Postmark (see Marketing sending domains)."
      action={
        <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Secondary onClick={() => handleOAuthConnect('microsoft')} disabled={connecting !== null}>
            {connecting === 'microsoft' ? 'Redirecting…' : 'Connect Microsoft 365'}
          </Secondary>
          <Secondary onClick={() => handleOAuthConnect('google')} disabled={connecting !== null}>
            {connecting === 'google' ? 'Redirecting…' : 'Connect Google'}
          </Secondary>
          <Primary onClick={() => setModalOpen(true)}>
            <Plus /> <span style={{ marginLeft: 6 }}>Custom server</span>
          </Primary>
        </div>
      }
    >
      <div
        style={{
          padding: 'var(--space-3) var(--space-4)',
          borderRadius: 'var(--radius-input)',
          backgroundColor: 'var(--color-surface-muted)',
          border: '1px solid var(--color-border-default)',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-xs)',
          color: 'var(--color-text-secondary)',
          lineHeight: 'var(--leading-relaxed)',
        }}
      >
        <strong style={{ color: 'var(--color-text-primary)' }}>Channel isolation:</strong>{' '}
        these mailboxes are used exclusively for 1-to-1 outreach. Bounces and
        unsubscribes from outreach do not affect newsletter eligibility, and
        vice versa.
      </div>

      {isLoading && <Muted>Loading mailboxes…</Muted>}

      {!isLoading && mailboxes.length === 0 && (
        <EmptyState onAdd={() => setModalOpen(true)} />
      )}

      {!isLoading && mailboxes.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {mailboxes.map((m) => <MailboxRow key={m.id} mailbox={m} />)}
        </ul>
      )}

      {modalOpen && <AddMailboxModal onClose={() => setModalOpen(false)} />}
    </Shell>
  );
}

/* ── Row ── */

function MailboxRow({ mailbox }: { mailbox: SmtpMailbox }) {
  const retest = useRetestSmtpMailbox();
  const del = useDeleteSmtpMailbox();
  const currentUserId = useAuthStore((s) => s.user?.user_id);
  const overLimit = mailbox.daily_limit > DAILY_LIMIT_WARNING;
  const [calendarExpanded, setCalendarExpanded] = useState(false);
  const linkage = mailboxLinkage(mailbox, currentUserId);

  return (
    <li
      style={{
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-input)',
        border: '1px solid var(--color-border-default)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
      }}
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 'var(--space-4)', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', minWidth: 0 }}>
          <span
            aria-hidden="true"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 'var(--space-9)',
              height: 'var(--space-9)',
              borderRadius: 'var(--radius-input)',
              backgroundColor: 'var(--color-blue-50)',
              color: 'var(--color-brand-blue)',
            }}
          >
            <MailIcon size={16} />
          </span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>
              {mailbox.label}
            </span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
              {mailbox.email_address}
            </span>
            <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 4, flexWrap: 'wrap' }}>
              <StatusChip status={mailbox.status} />
              <Tag>{providerLabel(mailbox)}</Tag>
              <Tag>{mailbox.auth_type === 'OAUTH' ? 'OAuth' : 'Password'}</Tag>
              <Tag tone={linkage.tone}>{linkage.label}</Tag>
              <Tag tone={overLimit ? 'warn' : 'idle'}>
                {mailbox.sends_today} / {mailbox.daily_limit} sent today
              </Tag>
              <Tag tone={mailbox.calendar_link ? 'idle' : 'warn'}>
                {mailbox.calendar_link ? 'Calendar link set' : 'No calendar link'}
              </Tag>
            </div>
            <span style={{ marginTop: 4, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
              {linkage.detail}
            </span>
            {mailbox.last_error && (
              <span style={{ marginTop: 4, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-warning)' }}>
                {mailbox.last_error}
              </span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
          <Secondary small onClick={() => setCalendarExpanded((v) => !v)}>
            {calendarExpanded ? 'Hide calendar' : 'Calendar link'}
          </Secondary>
          {(mailbox.status === 'ERROR' || mailbox.status === 'UNVERIFIED') && (
            <Secondary small onClick={() => retest.mutate(mailbox.id)} disabled={retest.isPending}>
              {retest.isPending ? 'Testing…' : 'Re-test'}
            </Secondary>
          )}
        </div>

        <div>
          <Secondary
            small
            onClick={() => {
              if (window.confirm(`Disconnect ${mailbox.label}? Campaigns using this mailbox will pause.`)) {
                del.mutate(mailbox.id);
              }
            }}
            disabled={del.isPending}
          >
            Disconnect
          </Secondary>
        </div>
      </div>

      {calendarExpanded && <CalendarLinkEditor mailbox={mailbox} />}
    </li>
  );
}

/**
 * Inline editor for the mailbox's {{calendar_link}} value.
 * Two paths: paste a URL manually, or sync the recruiter's first event-type
 * from JT. Sync requires the user's stored JT token (same one used by manual
 * jt-sync) — we read it from the auth store and pass it in the body.
 */
function CalendarLinkEditor({ mailbox }: { mailbox: SmtpMailbox }) {
  const { addToast } = useToast();
  const jtToken = useAuthStore((s) => s.jtToken);
  const setJtTokenInStore = useAuthStore((s) => s.setJtToken);
  const update = useUpdateSmtpMailbox(mailbox.id);
  const sync = useSyncMailboxCalendarLink();
  const [draft, setDraft] = useState<string>(mailbox.calendar_link ?? '');

  const handleSave = async () => {
    try {
      await update.mutateAsync({ calendar_link: draft.trim() || null });
      addToast({ variant: 'success', title: 'Calendar link saved' });
    } catch {
      addToast({ variant: 'danger', title: 'Could not save calendar link' });
    }
  };

  const handleSync = async () => {
    let token = jtToken;
    if (!token) {
      token = window.prompt('Paste your JT access token to sync your booking link:');
      if (!token) return;
      setJtTokenInStore(token.trim());
    }
    try {
      const result = await sync.mutateAsync({ mailboxId: mailbox.id, jt_token: token });
      setDraft(result.calendar_link ?? '');
      addToast({ variant: 'success', title: 'Pulled booking link from JT' });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      if (status === 401) {
        setJtTokenInStore(null);
        addToast({ variant: 'danger', title: 'JT token rejected', description: 'Re-launch Engage from JT and try again.' });
      } else if (status === 404) {
        addToast({ variant: 'warning', title: 'No event types in JT', description: msg ?? 'Create one in your JT account first.' });
      } else {
        addToast({ variant: 'danger', title: 'Sync failed', description: msg ?? 'Try again in a moment.' });
      }
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-input)',
        backgroundColor: 'var(--color-surface-muted)',
        border: '1px solid var(--color-border-default)',
      }}
    >
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)' }}>
        Calendar booking link
      </span>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
        Surfaced as <code>{'{{calendar_link}}'}</code> in email and task templates. Sync pulls your first JT event-type, or paste any other URL (Calendly, Cal.com, etc.).
      </span>
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <input
          type="url"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="https://app.jobtetris.com/event-scheduler/events/…"
          style={{
            flex: 1,
            height: 'var(--input-height)',
            padding: '0 var(--space-3)',
            border: '1px solid var(--input-border)',
            borderRadius: 'var(--input-radius)',
            backgroundColor: 'var(--input-bg)',
            fontSize: 'var(--input-font-size)',
            fontFamily: 'var(--font-sans)',
            color: 'var(--color-text-primary)',
            outline: 'none',
          }}
        />
        <Secondary small onClick={handleSync} disabled={sync.isPending}>
          {sync.isPending ? 'Syncing…' : 'Sync from JT'}
        </Secondary>
        <Primary onClick={handleSave} disabled={update.isPending}>
          {update.isPending ? 'Saving…' : 'Save'}
        </Primary>
      </div>
    </div>
  );
}

/* ── Empty state ── */

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div
      style={{
        padding: 'var(--space-6)',
        borderRadius: 'var(--radius-input)',
        border: '1px dashed var(--color-border-strong)',
        backgroundColor: 'var(--color-surface-muted)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 'var(--space-3)',
      }}
    >
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>
        No mailboxes connected yet
      </span>
      <Muted>
        Outreach campaigns need at least one SMTP-connected mailbox. Gmail, Microsoft 365, Outlook, Zoho and any
        standard SMTP server are supported.
      </Muted>
      <Primary onClick={onAdd}>Connect your first mailbox</Primary>
    </div>
  );
}

/* ── Add modal ── */

type ProviderPreset = 'gmail' | 'm365' | 'custom';

const PRESETS: Record<ProviderPreset, { smtp_host: string; smtp_port: number; imap_host: string; imap_port: number }> = {
  gmail:  { smtp_host: 'smtp.gmail.com',         smtp_port: 587, imap_host: 'imap.gmail.com',          imap_port: 993 },
  m365:   { smtp_host: 'smtp.office365.com',     smtp_port: 587, imap_host: 'outlook.office365.com',   imap_port: 993 },
  custom: { smtp_host: '',                       smtp_port: 587, imap_host: '',                        imap_port: 993 },
};

function AddMailboxModal({ onClose }: { onClose: () => void }) {
  const create = useCreateSmtpMailbox();
  const [provider, setProvider] = useState<ProviderPreset>('gmail');
  const [form, setForm] = useState<CreateMailboxPayload>({
    label: '',
    email_address: '',
    smtp_host: PRESETS.gmail.smtp_host,
    smtp_port: PRESETS.gmail.smtp_port,
    smtp_username: '',
    smtp_password: '',
    imap_host: PRESETS.gmail.imap_host,
    imap_port: PRESETS.gmail.imap_port,
    imap_username: '',
    imap_password: '',
    is_shared: false,
    daily_limit: DEFAULT_DAILY_LIMIT,
  });
  const [error, setError] = useState<string | null>(null);

  function pickProvider(p: ProviderPreset) {
    setProvider(p);
    const preset = PRESETS[p];
    setForm((f) => ({
      ...f,
      smtp_host: preset.smtp_host,
      smtp_port: preset.smtp_port,
      imap_host: preset.imap_host,
      imap_port: preset.imap_port,
    }));
  }

  function patch<K extends keyof CreateMailboxPayload>(key: K, value: CreateMailboxPayload[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit() {
    setError(null);

    if (!form.label || !form.email_address || !form.smtp_host || !form.smtp_username || !form.smtp_password) {
      setError('Label, email and SMTP credentials are required.');
      return;
    }

    // Default IMAP user to SMTP user when omitted — most providers use the same login.
    const payload: CreateMailboxPayload = {
      ...form,
      imap_username: form.imap_username || form.smtp_username,
      imap_password: form.imap_password || form.smtp_password,
    };

    try {
      await create.mutateAsync(payload);
      onClose();
    } catch (err) {
      const message = (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
        ?? (err as Error).message
        ?? 'Failed to connect mailbox';
      setError(message);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="mailbox-modal-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 'var(--z-modal)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--modal-overlay-bg)',
        padding: 'var(--space-4)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 720,
          maxHeight: '90vh',
          overflowY: 'auto',
          backgroundColor: 'var(--card-bg)',
          borderRadius: 'var(--card-radius)',
          boxShadow: 'var(--shadow-xl, 0 24px 48px rgba(0,0,0,0.12))',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: 'var(--space-5) var(--space-6)',
            borderBottom: '1px solid var(--color-border-default)',
          }}
        >
          <div>
            <h2 id="mailbox-modal-title" style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)' }}>
              Connect a mailbox
            </h2>
            <p style={{ marginTop: 4, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
              Engage will run a connection test once you save. Credentials are encrypted at rest.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 32,
              height: 32,
              border: '1px solid var(--color-border-default)',
              borderRadius: 'var(--btn-radius)',
              background: 'transparent',
              color: 'var(--color-text-muted)',
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </header>

        <div style={{ padding: 'var(--space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
          <Field label="Provider">
            <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <ChipButton active={provider === 'gmail'}  onClick={() => pickProvider('gmail')}>Gmail / Workspace</ChipButton>
              <ChipButton active={provider === 'm365'}   onClick={() => pickProvider('m365')}>Microsoft 365</ChipButton>
              <ChipButton active={provider === 'custom'} onClick={() => pickProvider('custom')}>Other SMTP</ChipButton>
            </div>
            {provider === 'gmail' && (
              <Help>Gmail requires an App Password. Enable 2-Step Verification in your Google Account first.</Help>
            )}
            {provider === 'm365' && (
              <Help>Microsoft 365 may require SMTP AUTH to be enabled by your admin. Contact your IT team if connection fails.</Help>
            )}
          </Field>

          <Row>
            <Field label="Label" hint="Shown in the campaign builder when assigning a mailbox.">
              <Input value={form.label} onChange={(v) => patch('label', v)} placeholder="e.g. Sienna outreach" />
            </Field>
            <Field label="From email" hint="The address candidates will see as sender.">
              <Input value={form.email_address} onChange={(v) => patch('email_address', v)} placeholder="sienna@acme.com" type="email" />
            </Field>
          </Row>

          <Field label="Mailbox type">
            <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
              <ChipButton active={!form.is_shared} onClick={() => patch('is_shared', false)}>Personal</ChipButton>
              <ChipButton active={!!form.is_shared} onClick={() => patch('is_shared', true)}>Shared</ChipButton>
            </div>
            <Help>
              Personal mailboxes are tied to your account. Shared mailboxes (e.g. a team BD inbox) can be assigned to
              campaigns by anyone with Write or Publish role.
            </Help>
          </Field>

          <Field
            label={`Daily send limit · ${form.daily_limit}`}
            hint={
              (form.daily_limit ?? 0) > DAILY_LIMIT_WARNING
                ? 'Above 50 may impact sender reputation. Step up gradually as the mailbox warms.'
                : 'Default 50. Lower while a new mailbox is warming up.'
            }
          >
            <input
              type="range"
              min={MIN_DAILY_LIMIT}
              max={MAX_DAILY_LIMIT}
              value={form.daily_limit}
              onChange={(e) => patch('daily_limit', parseInt(e.target.value, 10))}
              style={{ width: '100%' }}
            />
          </Field>

          <FieldGroup title="SMTP (outbound)">
            <Row>
              <Field label="SMTP host"><Input value={form.smtp_host} onChange={(v) => patch('smtp_host', v)} /></Field>
              <Field label="Port">
                <Input value={String(form.smtp_port)} onChange={(v) => patch('smtp_port', parseInt(v || '0', 10))} type="number" />
              </Field>
            </Row>
            <Row>
              <Field label="Username"><Input value={form.smtp_username} onChange={(v) => patch('smtp_username', v)} /></Field>
              <Field label={provider === 'gmail' ? 'App password' : 'Password'}>
                <Input value={form.smtp_password} onChange={(v) => patch('smtp_password', v)} type="password" />
              </Field>
            </Row>
          </FieldGroup>

          <FieldGroup title="IMAP (reply detection)" hint="Used to detect replies, bounces and out-of-office. Defaults to the SMTP login if blank.">
            <Row>
              <Field label="IMAP host"><Input value={form.imap_host} onChange={(v) => patch('imap_host', v)} /></Field>
              <Field label="Port">
                <Input value={String(form.imap_port)} onChange={(v) => patch('imap_port', parseInt(v || '0', 10))} type="number" />
              </Field>
            </Row>
            <Row>
              <Field label="Username (optional)"><Input value={form.imap_username ?? ''} onChange={(v) => patch('imap_username', v)} /></Field>
              <Field label="Password (optional)">
                <Input value={form.imap_password ?? ''} onChange={(v) => patch('imap_password', v)} type="password" />
              </Field>
            </Row>
          </FieldGroup>

          {error && (
            <div
              role="alert"
              style={{
                padding: 'var(--space-3) var(--space-4)',
                borderRadius: 'var(--radius-input)',
                border: '1px solid var(--color-warning)',
                backgroundColor: 'var(--color-warning-bg, rgba(220, 100, 50, 0.08))',
                color: 'var(--color-warning)',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-sm)',
              }}
            >
              {error}
            </div>
          )}
        </div>

        <footer
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 'var(--space-2)',
            padding: 'var(--space-4) var(--space-6)',
            borderTop: '1px solid var(--color-border-default)',
          }}
        >
          <Secondary onClick={onClose}>Cancel</Secondary>
          <Primary onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Connecting…' : 'Connect mailbox'}
          </Primary>
        </footer>
      </div>
    </div>
  );
}

/* ── Local primitives ── */

function Shell({
  title, subtitle, action, children,
}: { title: string; subtitle: string; action?: ReactNode; children: ReactNode }) {
  return (
    <section style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 'var(--card-radius)', boxShadow: 'var(--card-shadow)', overflow: 'hidden' }}>
      <header style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-3)', padding: 'var(--space-5) var(--space-6)', borderBottom: '1px solid var(--color-border-default)' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)', minWidth: 0 }}>
          <h2 style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)', letterSpacing: 'var(--tracking-tight)' }}>{title}</h2>
          <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{subtitle}</p>
        </div>
        {action}
      </header>
      <div style={{ padding: 'var(--space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {children}
      </div>
    </section>
  );
}

/**
 * Describes who a mailbox is linked to, so the recruiter can tell at a glance
 * whether they're looking at their own personal account, another team member's,
 * or a shared organisation mailbox. `is_shared` mailboxes belong to the org;
 * otherwise the mailbox is tied to a single user (user_id).
 */
function mailboxLinkage(
  mailbox: SmtpMailbox,
  currentUserId: string | undefined,
): { label: string; detail: string; tone: 'idle' | 'verified' | 'warn' } {
  if (mailbox.is_shared) {
    return {
      label: 'Organization (shared)',
      detail: 'Linked to your organisation: any team member with Write or Publish access can send from this mailbox.',
      tone: 'verified',
    };
  }
  if (mailbox.user_id && currentUserId && mailbox.user_id === currentUserId) {
    return {
      label: 'Your account',
      detail: 'Personal mailbox linked to your individual account. Only you can send from it.',
      tone: 'idle',
    };
  }
  if (mailbox.user_id) {
    return {
      label: 'Personal (another member)',
      detail: 'Personal mailbox linked to another team member’s individual account.',
      tone: 'warn',
    };
  }
  return {
    label: 'Unlinked',
    detail: 'Not yet linked to a user or the organization. Re-connect to assign ownership.',
    tone: 'warn',
  };
}

function providerLabel(mailbox: SmtpMailbox): string {
  if (mailbox.auth_type === 'OAUTH') {
    if (mailbox.oauth_provider === 'MICROSOFT') return 'Microsoft 365';
    if (mailbox.oauth_provider === 'GOOGLE') return 'Gmail';
    return 'OAuth';
  }
  return 'Custom SMTP';
}

function StatusChip({ status }: { status: MailboxStatus }) {
  const map: Record<MailboxStatus, { label: string; tone: 'verified' | 'warming' | 'idle' | 'warn' }> = {
    ACTIVE:     { label: 'Verified',  tone: 'verified' },
    UNVERIFIED: { label: 'Testing…',  tone: 'warming' },
    PAUSED:     { label: 'Paused',    tone: 'idle' },
    ERROR:      { label: 'Error',     tone: 'warn' },
  };
  return <Tag tone={map[status].tone}>{map[status].label}</Tag>;
}

function Tag({ children, tone = 'idle' }: { children: ReactNode; tone?: 'verified' | 'warming' | 'idle' | 'warn' }) {
  const map = {
    verified: { bg: 'var(--chip-verified-bg)', fg: 'var(--chip-verified-text)', border: 'var(--chip-verified-border)' },
    warming:  { bg: 'var(--chip-warming-bg)',  fg: 'var(--chip-warming-text)',  border: 'var(--chip-warming-border)' },
    idle:     { bg: 'var(--chip-idle-bg)',     fg: 'var(--chip-idle-text)',     border: 'var(--chip-idle-border)' },
    warn:     { bg: 'var(--chip-warming-bg)',  fg: 'var(--color-warning)',      border: 'var(--color-warning)' },
  } as const;
  const s = map[tone];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', height: 'var(--chip-height)', padding: 'var(--chip-padding-y) var(--chip-padding-x)', borderRadius: 'var(--chip-radius)', backgroundColor: s.bg, border: `1px solid ${s.border}`, color: s.fg, fontFamily: 'var(--font-sans)', fontSize: 'var(--chip-font-size)', fontWeight: 'var(--chip-font-weight)', letterSpacing: 'var(--tracking-wide)' }}>
      {children}
    </span>
  );
}

function Muted({ children }: { children: ReactNode }) {
  return <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{children}</p>;
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', minWidth: 0 }}>
      <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-secondary)' }}>{label}</span>
      {children}
      {hint && <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{hint}</span>}
    </div>
  );
}

function FieldGroup({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)', padding: 'var(--space-4)', borderRadius: 'var(--radius-input)', border: '1px solid var(--color-border-default)' }}>
      <div>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>{title}</span>
        {hint && <p style={{ marginTop: 2, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{hint}</p>}
      </div>
      {children}
    </div>
  );
}

function Row({ children }: { children: ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>{children}</div>;
}

function Input({ value, onChange, type = 'text', placeholder }: { value: string; onChange: (v: string) => void; type?: string; placeholder?: string }) {
  return (
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      style={{
        height: 'var(--input-height, 38px)',
        padding: '0 var(--space-3)',
        borderRadius: 'var(--radius-input)',
        border: '1px solid var(--color-border-default)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-sm)',
        color: 'var(--color-text-primary)',
        backgroundColor: 'var(--color-surface-white)',
      }}
    />
  );
}

function Help({ children }: { children: ReactNode }) {
  return (
    <p style={{ marginTop: 'var(--space-2)', padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-input)', backgroundColor: 'var(--color-surface-muted)', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
      {children}
    </p>
  );
}

function ChipButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 'var(--btn-height)',
        padding: '0 var(--btn-padding-x)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: active ? 'var(--color-action-primary)' : 'var(--color-surface-white)',
        border: `1px solid ${active ? 'var(--color-action-primary)' : 'var(--color-border-default)'}`,
        color: active ? 'var(--color-action-primary-text)' : 'var(--color-text-secondary)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-sm)',
        fontWeight: 'var(--font-weight-semibold)',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function Primary({ children, onClick, disabled }: { children: ReactNode; onClick?: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: 'var(--btn-height)',
        padding: '0 var(--btn-padding-x)',
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'var(--color-action-primary)',
        color: 'var(--color-action-primary-text)',
        border: 'none',
        fontSize: 'var(--btn-font-size)',
        fontWeight: 'var(--btn-font-weight)',
        fontFamily: 'var(--font-sans)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
      } as CSSProperties}
    >
      {children}
    </button>
  );
}

function Secondary({ children, small, onClick, disabled }: { children: ReactNode; small?: boolean; onClick?: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: small ? 'var(--btn-height-sm)' : 'var(--btn-height)',
        padding: `0 ${small ? 'var(--btn-padding-x-sm)' : 'var(--btn-padding-x)'}`,
        borderRadius: 'var(--btn-radius)',
        backgroundColor: 'var(--color-action-secondary)',
        color: 'var(--color-action-secondary-text)',
        border: '1px solid var(--color-action-secondary-border)',
        fontSize: small ? 'var(--text-xs)' : 'var(--btn-font-size)',
        fontWeight: 'var(--font-weight-semibold)',
        fontFamily: 'var(--font-sans)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        whiteSpace: 'nowrap',
      } as CSSProperties}
    >
      {children}
    </button>
  );
}

function Plus() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function MailIcon({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22 6 12 13 2 6" />
    </svg>
  );
}
